import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useLanguage } from "../../context/LanguageContext";
import { Sparkles, Loader2, BookOpen, MessageCircleQuestion } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { api } from "../../lib/api";
import { toast } from "sonner";

/**
 * Weekly parasha chiddush — AI-generated 3-5 key points + reflection
 * question. Triggered from the homepage parasha card. The endpoint is
 * server-cached for 7 days per slug so opening it on Sunday and Friday
 * shows the SAME insight, which keeps the user experience coherent
 * across the week.
 */
export default function ParashaChiddushDialog({ parashaHe, parashaName }) {
  const { t } = useTranslation();
  const { isEn } = useLanguage();
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  // Remember which language the cached `data` was fetched in so a
  // user who switches HE↔EN mid-session re-fetches in the new lang
  // instead of staring at the old language's chiddush.
  const [dataLang, setDataLang] = useState(null);
  const [loading, setLoading] = useState(false);

  // Pick the locale-appropriate parasha name. EN callers may pass
  // `parashaName` (Sefaria slug, e.g. "Korach"); HE callers pass
  // `parashaHe` ("קרח"). Either field is enough on its own — we fall
  // back gracefully so any caller that hasn't been updated yet still
  // works in Hebrew exactly as before.
  const displayName = isEn ? (parashaName || parashaHe) : (parashaHe || parashaName);

  const launch = async (e) => {
    // We sit inside a parent <Link> that navigates to the reader — stop
    // the click from triggering navigation.
    e?.preventDefault?.();
    e?.stopPropagation?.();
    setOpen(true);
    const wantedLang = isEn ? "en" : "he";
    if (data && dataLang === wantedLang) return; // already generated for this language
    setLoading(true);
    try {
      const res = await api.parashaCurrentInsight(wantedLang);
      setData(res);
      setDataLang(wantedLang);
    } catch (err) {
      toast.error(t("parasha_chiddush.error_title"), {
        description: err?.response?.data?.detail || t("parasha_chiddush.error_description"),
      });
      setOpen(false);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Button
        onClick={launch}
        variant="outline"
        size="sm"
        className="border-gold/40 hover:bg-gold/10 hover:border-gold text-foreground gap-2 whitespace-normal h-auto py-2.5 text-xs sm:text-sm leading-snug"
        data-testid="parasha-chiddush-btn"
      >
        <Sparkles className="h-4 w-4 shrink-0 text-gold" strokeWidth={1.5} />
        <span>{t("parasha_chiddush.weekly_insight")}</span>
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent
          className="max-w-xl max-h-[90vh] overflow-y-auto"
          dir={isEn ? "ltr" : "rtl"}
          data-testid="parasha-chiddush-dialog"
        >
          <DialogHeader className={isEn ? "text-left" : "text-right"}>
            <div className="text-[10px] tracking-[0.3em] uppercase text-gold/80 mb-1">
              {t("parasha_chiddush.weekly_insight")}
            </div>
            <DialogTitle className="font-heading text-2xl text-foreground">
              {displayName
                ? t("parasha_chiddush.parasha_name", { name: displayName })
                : t("parasha_chiddush.this_week_parasha")}
            </DialogTitle>
            <DialogDescription className="text-xs text-muted-foreground">
              {t("parasha_chiddush.description")}
            </DialogDescription>
          </DialogHeader>

          {loading ? (
            <div
              className="grid place-items-center py-14 space-y-3"
              data-testid="parasha-chiddush-loading"
            >
              <Loader2 className="h-6 w-6 animate-spin text-gold" />
              <p className="text-xs text-muted-foreground mt-3">
                {t("parasha_chiddush.loading")}
              </p>
            </div>
          ) : !data ? null : (
            <div className="space-y-5 mt-2">
              {/* Headline */}
              <div className="text-center">
                <div className="ornamental-divider mx-auto max-w-xs text-gold">
                  ✦
                </div>
                <h3
                  className="font-heading text-xl text-gold leading-snug mt-2"
                  data-testid="chiddush-headline"
                >
                  {data.headline}
                </h3>
              </div>

              {/* Key points */}
              <section>
                <div className={`flex items-center gap-2 text-[11px] tracking-[0.2em] uppercase text-gold/80 mb-2 ${isEn ? "justify-start" : "justify-end"}`}>
                  <span>{t("parasha_chiddush.key_points")}</span>
                  <BookOpen className="h-3.5 w-3.5" strokeWidth={1.5} />
                </div>
                <ul
                  className="space-y-2.5"
                  data-testid="chiddush-key-points"
                >
                  {(data.key_points || []).map((pt, i) => (
                    <li
                      key={i}
                      className={`flex items-start gap-3 rounded-md bg-card/40 border border-border/40 p-3 leading-relaxed text-sm ${isEn ? "text-left" : "text-right"}`}
                    >
                      <span className="font-heading text-gold text-base leading-none mt-1 flex-shrink-0">
                        {i + 1}
                      </span>
                      <span className="text-foreground">{pt}</span>
                    </li>
                  ))}
                </ul>
              </section>

              {/* Reflection question */}
              <section className="rounded-md border border-gold/30 bg-gold/5 p-4">
                <div className={`flex items-center gap-2 text-[11px] tracking-[0.2em] uppercase text-gold/80 mb-2 ${isEn ? "justify-start" : "justify-end"}`}>
                  <span>{t("parasha_chiddush.reflection_question")}</span>
                  <MessageCircleQuestion className="h-3.5 w-3.5" strokeWidth={1.5} />
                </div>
                <p
                  className={`text-sm leading-relaxed text-foreground ${isEn ? "text-left" : "text-right"}`}
                  data-testid="chiddush-reflection"
                >
                  {data.reflection}
                </p>
              </section>

              <p className="text-[10px] text-muted-foreground text-center pt-1">
                {t("parasha_chiddush.footer_note")}
              </p>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
